import React from 'react';
import {
  Zap, BarChart3, FileText, Shield, Sparkles,
  ArrowRight, CheckCircle, Globe, Users, TrendingUp, Star,
} from 'lucide-react';
import { MONTHLY_PRICE, FIRST_MONTH_PRICE, FIRST_MONTH_DISCOUNT_PERCENT } from '../constants/pricing';

interface Props { onGetStarted: () => void; }

const features = [
  {
    icon: Sparkles,
    title: 'Konektra AI-Powered',
    desc: 'Instant quote generation from plain English. Upload competitor bills for instant analysis.',
    color: 'from-purple-500 to-violet-600',
  },
  {
    icon: BarChart3,
    title: 'Real-Time Comparison',
    desc: 'Side-by-side ROI charts vs. Verizon, AT&T, and other carriers. Visual savings breakdown.',
    color: 'from-blue-500 to-cyan-600',
  },
  {
    icon: FileText,
    title: 'Instant PDF Quotes',
    desc: 'Professional, branded quotes ready to share in seconds. Custom seller info on every page.',
    color: 'from-emerald-500 to-teal-600',
  },
  {
    icon: Shield,
    title: 'AI Objection Handling',
    desc: 'Real-time sales coaching with objection responses and carrier comparison data.',
    color: 'from-orange-500 to-amber-600',
  },
];

const stats = [
  { label: 'Active Partners',   value: '500+',   icon: Users },
  { label: 'Quotes Generated',  value: '12,400+', icon: FileText },
  { label: 'Avg. Monthly Save', value: '$2,800',  icon: TrendingUp },
  { label: 'Win Rate',          value: '94%',     icon: Star },
];

const testimonials = [
  { name: 'Marcus T.', role: 'Regional Sales Manager', quote: 'Closed a 200-line deal in one meeting. The AI bill analysis floored the customer.' },
  { name: 'Priya R.',  role: 'Channel Partner',        quote: 'ProQuote cut my proposal time from 2 hours to 8 minutes. Game changer.' },
  { name: 'Jordan K.', role: 'Business Solutions Rep',  quote: 'The objection handling scripts alone are worth it. My close rate went up 40%.' },
];

export const LandingPage: React.FC<Props> = ({ onGetStarted }) => (
  <div className="min-h-screen bg-[#0A0A0F] text-white overflow-hidden">
    {/* Nav */}
    <nav className="fixed top-0 inset-x-0 z-50 flex items-center justify-between px-6 py-4 bg-[#0A0A0F]/80 backdrop-blur-xl border-b border-white/5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#0A0A0F] flex items-center justify-center shadow-lg shadow-purple-500/30 overflow-hidden">
            <img src="/nxtkonekt-logo.svg" alt="NxtKonekt" className="w-full h-full" />
          </div>
        <span className="font-black text-lg tracking-tight">
          Nxt<span className="text-purple-400">Konekt</span>
          <span className="text-xs font-medium text-gray-500 ml-1.5">ProQuote</span>
        </span>
      </div>
      <button
        onClick={onGetStarted}
        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-sm font-semibold transition-all duration-200 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105"
      >
        Partner Login <ArrowRight size={14} />
      </button>
    </nav>

    {/* Hero */}
    <section className="relative min-h-screen flex flex-col items-center justify-center px-6 pt-20">
      {/* Background glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-purple-600/10 blur-[120px]" />
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] rounded-full bg-violet-500/8 blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] rounded-full bg-blue-500/8 blur-[80px]" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(124,58,237,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center animate-fade-in">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-8">
          <Sparkles size={14} className="animate-pulse" />
          Powered by Konektra AI
        </div>

        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-6">
          Quote Smarter.{' '}
          <span className="bg-gradient-to-r from-purple-400 via-violet-400 to-indigo-400 bg-clip-text text-transparent">
            Close Faster.
          </span>
        </h1>

        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
          NxtKonekt ProQuote is the AI-powered quoting platform built exclusively for authorized telecom partners.
          Generate proposals, analyze competitor bills, and win more business — in minutes, not hours.
        </p>

        {/* Pricing pill */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-2xl bg-white/5 border border-white/10">
            <span className="text-3xl font-black text-white">${MONTHLY_PRICE}</span>
            <div className="text-left">
              <div className="text-xs text-gray-400 leading-tight">per month</div>
              <div className="text-xs text-emerald-400 font-semibold leading-tight">First month {FIRST_MONTH_DISCOUNT_PERCENT}% off</div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={onGetStarted}
            className="group flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 text-white font-bold text-lg transition-all duration-300 shadow-2xl shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105"
          >
            <Zap size={20} className="group-hover:animate-pulse" />
            Half off First Month
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <button className="flex items-center gap-2 px-6 py-4 rounded-2xl border border-white/10 hover:border-purple-500/40 hover:bg-white/5 text-gray-300 font-semibold transition-all duration-200">
            <Globe size={18} /> Watch Demo
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-20">
          {stats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center p-5 rounded-2xl bg-white/5 border border-white/8 backdrop-blur-sm hover:border-purple-500/30 transition-all duration-300">
              <Icon size={20} className="text-purple-400 mb-2" />
              <div className="text-3xl font-black text-white">{value}</div>
              <div className="text-xs text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Features */}
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-black mb-4">Everything you need to <span className="text-purple-400">dominate your territory</span></h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">Built by telecom veterans, powered by AI. NxtKonekt ProQuote gives your team an unfair advantage.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map(({ icon: Icon, title, desc, color }) => (
            <div key={title} className="group p-6 rounded-2xl bg-white/5 border border-white/8 hover:border-purple-500/30 hover:bg-white/8 transition-all duration-300 hover:-translate-y-1">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                <Icon size={22} className="text-white" />
              </div>
              <h3 className="font-bold text-white mb-2">{title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonials */}
    <section className="py-16 px-6 bg-gradient-to-b from-transparent to-purple-950/10">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-3xl font-black text-center mb-12">Loved by NxtKonekt partners</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {testimonials.map(({ name, role, quote }) => (
            <div key={name} className="p-6 rounded-2xl bg-white/5 border border-white/8">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-amber-400 fill-amber-400" />)}
              </div>
              <p className="text-gray-300 text-sm leading-relaxed mb-4">"{quote}"</p>
              <div>
                <div className="font-semibold text-white text-sm">{name}</div>
                <div className="text-gray-500 text-xs">{role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA Banner */}
    <section className="py-20 px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="p-12 rounded-3xl bg-gradient-to-br from-purple-900/50 to-violet-900/30 border border-purple-500/20 backdrop-blur-sm">
          <h2 className="text-4xl font-black mb-4">Ready to close more deals?</h2>
          <p className="text-gray-400 mb-2">Join 500+ NxtKonekt partners already using ProQuote to win more business every day.</p>

          {/* Pricing callout */}
          <div className="inline-flex items-baseline gap-2 my-5 px-6 py-3 rounded-2xl bg-white/5 border border-purple-500/20">
            <span className="text-4xl font-black text-white">${MONTHLY_PRICE}</span>
            <span className="text-gray-400 text-sm">/month</span>
            <span className="ml-2 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/20">
              First month {FIRST_MONTH_DISCOUNT_PERCENT}% off — just ${FIRST_MONTH_PRICE.toFixed(2)}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            {['No setup fee', 'Instant access', 'All carriers supported'].map(t => (
              <span key={t} className="flex items-center gap-2 text-sm text-gray-400">
                <CheckCircle size={14} className="text-emerald-400" /> {t}
              </span>
            ))}
          </div>
          <button
            onClick={onGetStarted}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-500 hover:to-violet-500 font-bold text-lg transition-all duration-300 shadow-xl shadow-purple-500/30 hover:scale-105"
          >
            Half off First Month
          </button>
        </div>
      </div>
    </section>

    {/* Footer */}
    <footer className="py-8 px-6 border-t border-white/5 text-center text-gray-600 text-sm">
      © {new Date().getFullYear()} NxtKonekt. All rights reserved.
    </footer>
  </div>
);
